import { Theme, createTheme } from '@mui/material/styles';

import { colors } from './colors';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    header1: React.CSSProperties;
    title1: React.CSSProperties;
    text1: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    header1?: React.CSSProperties;
    title1?: React.CSSProperties;
    text1?: React.CSSProperties;
  }
  interface Palette {
    brand: Palette['primary'];
    neutral: {
      [key: string]: string;
    };
  }
  interface PaletteOptions {
    brand?: PaletteOptions['primary'];
    neutral?: {
      [key: string]: string;
    };
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    header1: true;
    title1: true;
    text1: true;
  }
}

const theme: Theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.brand300, // #890096
      light: colors.brand100,
      dark: colors.brand500,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.secondary, // #259B91
      contrastText: colors.white,
    },
    error: {
      main: colors.error, // #F04923
    },
    success: {
      main: colors.success, // #17A975
    },
    warning: {
      main: colors.red300,
    },
    background: {
      default: colors.pageBg, // #060912
      paper: colors.bgDark, // #14141A
    },
    text: {
      primary: colors.white,
      secondary: colors.neutral90, // #9C9BA3
      disabled: colors.neutral500,
    },
    divider: colors.neutral015,
    // Brand colors
    brand: {
      main: colors.brand300,
      light: colors.brand100,
      dark: colors.brand500,
      contrastText: colors.white,
    },
    // Neutral colors
    neutral: {
      0: colors.neutral0,
      10: colors.neutral10,
      20: colors.neutral20,
      30: colors.neutral30,
      40: colors.neutral40,
      50: colors.neutral50,
      60: colors.neutral60,
      70: colors.neutral70,
      80: colors.neutral80,
      90: colors.neutral90,
      100: colors.neutral100,
      200: colors.neutral200,
      300: colors.neutral300,
      400: colors.neutral400,
      500: colors.neutral500,
      600: colors.neutral600,
      700: colors.neutral700,
      800: colors.neutral800,
      900: colors.neutral900,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1224,
      xl: 1440,
    },
  },
  typography: {
    fontFamily: 'Poppins, sans-serif',
    // Custom typography variants based on Figma
    h1: {
      fontFamily: 'Poppins',
      fontWeight: 700,
      fontSize: '32px',
      lineHeight: '48px',
    },
    h2: {
      fontFamily: 'Poppins',
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '28px',
    },
    h3: {
      fontFamily: 'Poppins',
      fontWeight: 700,
      fontSize: '20px',
      lineHeight: '30px',
    },
    h4: {
      fontFamily: 'Poppins',
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '20px',
    },
    h5: {
      fontFamily: 'Poppins',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '20px',
    },
    body1: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '20px',
    },
    body2: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
    },
    // Custom variants
    header1: {
      fontFamily: 'Poppins',
      fontWeight: 700,
      fontSize: '20px',
      lineHeight: '24px',
      letterSpacing: '-0.32px',
    },
    title1: {
      fontFamily: 'Poppins',
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '20px',
      letterSpacing: '-0.32px',
    },
    text1: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '17px',
      letterSpacing: '-0.16px',
    },
  },
  shape: {
    borderRadius: 4, // 4px border radius from Figma
  },
  spacing: 8, // 8px base spacing unit
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '4px',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: colors.brand300,
          '&:hover': {
            backgroundColor: colors.brand200,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.pageBg,
          color: colors.white,
        },
      },
    },
  },
});

export { theme };
